import {
  LayoutFeatureDetails,
  LayoutFeatureType,
  LocationPortal,
  PlaneAccessSpec,
  PlaneConnectivityGraph,
} from '@warebee/shared/engine-model';
import { MappingSchema } from '../mapping';

export class LayoutLocationAthena {
  locationId: string;

  locationOrder: string; // may exceed js int
  locationStatus: boolean;
  locationLevel: number;

  locationLength: number;
  locationWidth: number;
  locationHeight: number;
  locationWeight: number;

  locationBayId: string;
  locationBayTitle: string;

  locationBayPosition: number;
  locationDepthPosition: number;

  locationUsageType: string;
  locmhtype?: string;
  locationRackingType: string;
  warehouseArea: string;
  locationSide?: string;
  congestionZone?: string;
  locationBayProjection: number;
  locationHeightFromFloor: number;
  locationDepthFromFront: number;
  locationIndexFromFront: number;

  bayType?: string;

  aisleId: string;
  aisleTitle: string;

  planeId: string;
  planeTitle: string;

  shape?: GeoJSON.Polygon;
  portals?: LocationPortal[];
}

export const LAYOUT_LOCATION_MAPPING_SCHEMA: MappingSchema<LayoutLocationAthena> = {
  title: 'Layout location',
  description: 'Location on layout, such as storage location',
  fields: [
    {
      name: 'locationId',
      type: 'string',
      title: 'Location ID',
      description: 'Location Name, typically in the format AA010101',
    },
    {
      name: 'locationOrder',
      type: 'string',
      title: 'Location Pick Order',
      description:
        'Location picking order (picking path sequence). NUMBERS ONLY!',
    },
    {
      name: 'locationStatus',
      type: 'boolean',
      title: 'Status',
      description: 'Active Locations vs. Non-Active as (True/False)',
      
    },
    {
      name: 'locationLevel',
      type: 'integer',
      title: 'Level',
      description: 'Level. Floor location has Level = 1.',
      effectiveEnum: true,
    },

    {
      name: 'locationLength',
      type: 'number',
      title: 'Location Depth (Length)',
      description: 'Depth or Length of the location.',
    },
    {
      name: 'locationWidth',
      type: 'number',
      title: 'Location Width',
      description:
        'Width of the location. Please be consistent in units of measure! Typically in CM or Inches',
    },
    {
      name: 'locationHeight',
      type: 'number',
      title: 'Location Height',
      description:
        'Height of the location. Please be consistent in units of measure! Typically in CM or Inches',
    },
    {
      name: 'locationWeight',
      type: 'number',
      title: 'Location Weight',
      description:
        'Weight capacity of the location. Please be consistent in units of measure! Typically in kg or lbs',
    },

    { name: 'locationBayId', type: 'string', title: 'Location Bay ID' },
    { name: 'locationBayTitle', type: 'string', title: 'Location Bay Title' },

    {
      name: 'locationBayPosition',
      type: 'integer',
      title: 'Location Bay Position',
      description:
        'Position of the location in the bay. Positions start from the left hand side of the bay if you face it.',
    },
    { name: 'locationDepthPosition', type: 'integer' },

    {
      name: 'locationUsageType',
      type: 'string',
      title: 'Usage Type',
      description:
        "Location usage type. Can be used for filtering locations. Typically has values like 'PICKING' or 'STORAGE'.",
      effectiveEnum: true,
    },
    {
      name: 'locmhtype',
      type: 'string',
      optional: true,
      title: 'MHE Type',
      description:
        'Type of material handling equipment that can access the location',
      effectiveEnum: true,
    },
    {
      name: 'locationRackingType',
      type: 'string',
      title: 'Location Type',
      description:
        'Location Profile Types: Typically used for defining different "Shelving" and "Pallets types". Helps to customise gaps and selection of locations in filters by different profiles.',
      effectiveEnum: true,
    },
    {
      name: 'warehouseArea',
      type: 'string',
      title: 'Area',
      description:
        'Warehouse Area, is usually a WareBee specific physical area defined and should not be used for calculating the metrics',
      effectiveEnum: true,
    },
    {
      name: 'locationSide',
      type: 'string',
      optional: true,
      title: 'Bay Side',
      description: 'Side of the bay in the Aisle. Can be "Left" or "Right".',
      effectiveEnum: true,
    },
    {
      name: 'congestionZone',
      type: 'string',
      optional: true,
      title: 'Workload Zone',
      description: `
        Also called Warehouse Area, Zone, etc.
        it's can be used to classify the location,
        can be Velocity Zone, Work Zone, Congestion zone, or any logical location zone,
        and is usually used to classify the putaway logic, storage policy and pick zone efficiency
        `,
      effectiveEnum: true,
    },
    { name: 'locationBayProjection', type: 'number' },
    { name: 'locationHeightFromFloor', type: 'number' },
    { name: 'locationDepthFromFront', type: 'number' },
    { name: 'locationIndexFromFront', type: 'integer' },

    {
      name: 'bayType',
      type: 'string',
      optional: true,
      title: 'Bay type',
      description:
        'Type of the Bay to manage bay weight capacity and other bay level info',
      effectiveEnum: true,
    },

    { name: 'aisleId', type: 'string', title: 'Aisle ID' },
    { name: 'aisleTitle', type: 'string', title: 'Aisle title' },

    { name: 'planeId', type: 'string' },
    { name: 'planeTitle', type: 'string' },

    {
      name: 'shape',
      type: 'string',
      optional: true,
      excludeFromAiPrompt: true,
    },
    {
      name: 'portals',
      type: 'string',
      optional: true,
      excludeFromAiPrompt: true,
    },
  ],
};

export class LayoutFeatureAthena {
  id: string;
  type: LayoutFeatureType;

  planeId: string;
  planeTitle: string;

  title?: string;
  shape?: GeoJSON.Polygon;
  innerShape?: GeoJSON.Polygon;

  navigable: boolean;
  details?: LayoutFeatureDetails;
}

export const LAYOUT_FEATURE_MAPPING_SCHEMA: MappingSchema<LayoutFeatureAthena> =
  {
    title: 'Layout feature',
    description: 'Layout feature, such as Aisle or Bay',
    fields: [
      { name: 'id', type: 'string' },
      { name: 'type', type: 'string' },
      { name: 'planeId', type: 'string' },
      { name: 'planeTitle', type: 'string' },
      { name: 'title', type: 'string', optional: true },
      {
        name: 'shape',
        type: 'string',
        optional: true,
        excludeFromAiPrompt: true,
      },
      {
        name: 'innerShape',
        type: 'string',
        optional: true,
        excludeFromAiPrompt: true,
      },
      { name: 'navigable', type: 'boolean' },
      { name: 'details', type: 'string', optional: true },
    ],
  };

export class LayoutPlaneAthena {
  id: string;
  title?: string;
  start?: PlaneAccessSpec;
  end?: PlaneAccessSpec;
  connectivityGraph?: PlaneConnectivityGraph;
  boundingBox?: GeoJSON.BBox;
  outlineShape?: GeoJSON.Geometry;
  levels?: number[];
}

export const LAYOUT_PLANE_MAPPING_SCHEMA: MappingSchema<LayoutPlaneAthena> = {
  title: 'Layout plane',
  description: 'Navigable plane of a layout, such as floor or building',
  fields: [
    { name: 'id', type: 'string' },
    { name: 'title', type: 'string', optional: true },
    { name: 'start', type: 'string', optional: true },
    { name: 'end', type: 'string', optional: true },
    {
      name: 'connectivityGraph',
      type: 'string',
      optional: true,
      excludeFromAiPrompt: true,
    },
    {
      name: 'boundingBox',
      type: 'string',
      optional: true,
      excludeFromAiPrompt: true,
    },
    {
      name: 'outlineShape',
      type: 'string',
      optional: true,
      excludeFromAiPrompt: true,
    },
    { name: 'levels', type: 'string', optional: true },
  ],
};

export interface LayoutDatabase {
  layoutLocation: LayoutLocationAthena & { datasetObjectId: string };
  layoutPlane: LayoutPlaneAthena & { datasetObjectId: string };
  layoutFeature: LayoutFeatureAthena & { datasetObjectId: string };
}
